import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { Link, useNavigate } from "react-router-dom";
import { Email, Password, Button } from "../../controls";
import { UserService } from "../../services";
import { isEmail } from "validator";
import { usePersistStore, useStore } from "../../store";
import CopyRight from "../../components/CopyRight";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import GoogleAuthButton from "../../components/GoogleAuthButton";
import { USER_BADGE } from "../../utils/enums";
import {
  captureRedirectFromUrl,
  consumePostLoginRedirect,
} from "../../utils/postLoginRedirect";

function Signin() {
  const navigate = useNavigate();
  const { setIsLoading } = useStore((s: any) => s);
  const {
    setUser,
    setToken,
    setWatchlist,
    setFollowing,
    setFollowers,
    setIsVerifiedUser,
    setIsBuyerProfileCompleted,
  } = usePersistStore((s: any) => s);
  const [values, setValues] = useState({
    email: "",
    password: "",
  });
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [disableButton, setDisableButton] = useState(false);

  // Stash `?redirectTo=` from the URL into sessionStorage so the value
  // survives a detour through /register before landing back here.
  useEffect(() => {
    captureRedirectFromUrl();
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]: value,
      });
    },
    [values],
  );

  const hanldeSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      if (!values?.email || !isEmail(values?.email))
        return setValidationMessage("Please provide a valid email address.");
      if (!values?.password)
        return setValidationMessage("Please provide your password.");

      setDisableButton(true);
      setIsLoading(true);
      let result = await UserService.login(values);
      if (result?.error) {
        setIsLoading(false);
        setDisableButton(false);
        setValidationMessage(result?.error);
        return;
      }

      setIsLoading(false);
      const user = result?.data?.user;
      setUser(user);
      setWatchlist(user?.favourites || []);
      setFollowing(user?.following || []);
      setFollowers(user?.followers || []);
      setToken(result?.data?.token);

      const isVerifiedUser: boolean = user?.badges?.find(
        (k: string) => k === USER_BADGE.VERIFIED,
      )
        ? true
        : false;
      setIsVerifiedUser(isVerifiedUser);

      if (
        user?.address1 &&
        user?.address2 &&
        user?.city &&
        user?.state &&
        user?.zip &&
        user?.name &&
        user?.phone &&
        user?.email
      )
        setIsBuyerProfileCompleted(true);
      else setIsBuyerProfileCompleted(false);

      navigate(consumePostLoginRedirect(), { replace: true });
    },
    [
      values,
      setUser,
      setWatchlist,
      setFollowing,
      setFollowers,
      setToken,
      setIsVerifiedUser,
      navigate,
      setIsLoading,
      setIsBuyerProfileCompleted,
    ],
  );

  const handleGoogleLogin = useCallback(
    async (response: any) => {
      setIsLoading(true);
      let result = await UserService.loginWithGoogle({
        credential: response?.credential,
      });
      if (result?.error) {
        setIsLoading(false);
        setValidationMessage(result?.error);
        return;
      }

      const user = result?.data?.user;
      setUser(user);
      setWatchlist(user?.favourites || []);
      setFollowing(user?.following || []);
      setFollowers(user?.followers || []);
      setToken(result?.data?.token);

      const isVerifiedUser: boolean = user?.badges?.find(
        (k: string) => k === USER_BADGE.VERIFIED,
      )
        ? true
        : false;
      setIsVerifiedUser(isVerifiedUser);

      if (
        user?.address1 &&
        user?.address2 &&
        user?.city &&
        user?.state &&
        user?.zip &&
        user?.name &&
        user?.phone &&
        user?.email
      )
        setIsBuyerProfileCompleted(true);
      else setIsBuyerProfileCompleted(false);

      setIsLoading(false);
      navigate(consumePostLoginRedirect(), { replace: true });
    },
    [
      setUser,
      setWatchlist,
      setFollowing,
      setFollowers,
      setToken,
      setIsVerifiedUser,
      navigate,
      setIsLoading,
      setIsBuyerProfileCompleted,
    ],
  );

  return (
    <div className="container">
      <div className="row h-100vh">
        <div className="col-md-5 loginLeft position-relative">
          <div className="d-flex align-items-center h-100 w-100">
            <div className="w-100">
              <div className="text-md-start text-center">
                <Link to="/">
                  <Logo width={100} height={100} />
                </Link>
              </div>
              <h1>Welcome to Boll-E&trade; Please login.</h1>
              <p>Log in to your account to continue</p>
              <div className="formMain">
                <Form>
                  <Email
                    value={values?.email}
                    onChange={handleInputChange}
                    autoComplete="username"
                  />
                  <Password
                    value={values?.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                  />
                  {validationMessage && (
                    <div className="validation-message mb-2">
                      {validationMessage}
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center">
                    <div key="remember me" className="">
                      <Form.Check
                        defaultChecked
                        type="checkbox"
                        id="Remember Me"
                        label="Remember Me"
                      />
                    </div>
                    <Link to="/forgot-password">Forgot password?</Link>
                  </div>
                  <Button
                    title="Sign In"
                    onClick={hanldeSubmit}
                    disabled={disableButton}
                  />
                </Form>
              </div>
              <div className="lineThrough">
                <span>Or login with</span>
              </div>
              <div className="d-flex justify-content-center mb-3">
                <GoogleAuthButton
                  onSuccess={handleGoogleLogin}
                  onError={() =>
                    setValidationMessage("Failed to login with Google")
                  }
                  text="signin_with"
                />
              </div>
              <div className="authThemeToggle">
                <ThemeToggle />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center registerMain gap-2">
            <span>New member?</span>
            <div className="d-flex gap-2">
              <Link to="/register">Register here</Link>
            </div>
          </div>
        </div>
        <div className="col-md-1"></div>
        <div className="col-md-6 position-relative">
          <div className="d-md-flex align-items-center h-100 d-none">
            <img src="/assets/imgs/signin.png" className="w-100" alt="" />
          </div>
          <CopyRight />
        </div>
      </div>
    </div>
  );
}

export default Signin;
